import React, { useState } from 'react';
import { toast } from 'sonner';
import { Clock, MapPin, Video, Phone, Briefcase, User, CalendarClock, XCircle, Check, ClipboardCheck, Loader2 } from 'lucide-react';
import { interviewsAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScheduleInterviewDialog } from './ScheduleInterviewDialog';
import { InterviewResultDialog } from './InterviewResultDialog';
import { STATUS, ACTIVE_STATUS, FORMATOS, fmtTime, fmtDateTime, placeHref } from './interviewUtils';

const ICONS = { presencial: MapPin, online: Video, telefone: Phone };
const RESULT = { aprovado: 'Aprovado', reprovado: 'Reprovado' };

export const InterviewCard = ({ interview: iv, onChange, showDate }) => {
  const [resched, setResched] = useState(false);
  const [result, setResult] = useState(false);
  const [busy, setBusy] = useState(false);
  const Icon = ICONS[iv.formato] || MapPin;
  const active = ACTIVE_STATUS.includes(iv.status);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  const cancel = () => {
    const motivo = window.prompt('Cancelar entrevista? Informe o motivo (opcional):');
    if (motivo === null) return;
    run(() => interviewsAPI.cancel(iv.id, { motivo }), 'Entrevista cancelada. O candidato foi avisado.');
  };

  return (
    <div className="rounded-xl border bg-white p-4 flex flex-col sm:flex-row gap-4" data-testid={`interview-card-${iv.id}`}>
      <div className="sm:w-28 shrink-0">
        <p className="text-2xl font-bold text-slate-900">{fmtTime(iv.data_hora)}</p>
        {showDate && <p className="text-xs text-slate-500">{fmtDateTime(iv.data_hora).split(',').slice(0, 2).join(',')}</p>}
      </div>
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-900 flex items-center gap-1"><User className="w-4 h-4 text-slate-400" />{iv.candidate_name || 'Candidato'}</span>
          <Badge className={STATUS[iv.status]?.cls} data-testid={`interview-status-${iv.id}`}>{STATUS[iv.status]?.label}</Badge>
          {iv.decisao && <Badge variant="outline" data-testid={`interview-decision-${iv.id}`}>{RESULT[iv.decisao]}</Badge>}
          {iv.comparecimento === 'faltou' && <Badge variant="outline" className="text-red-600">Faltou</Badge>}
        </div>
        <p className="text-sm text-slate-600 flex items-center gap-1"><Briefcase className="w-4 h-4 text-slate-400" />{iv.job_title}</p>
        <a href={placeHref(iv.formato, iv.local)} target="_blank" rel="noreferrer" className="text-sm text-cyan-700 hover:underline flex items-center gap-1 break-all">
          <Icon className="w-4 h-4 shrink-0" />{FORMATOS[iv.formato]?.label}: {iv.local}
        </a>
        {iv.observacoes && <p className="text-xs text-slate-500" data-testid={`interview-notes-${iv.id}`}>Obs. para o candidato: {iv.observacoes}</p>}
        {iv.status === 'CANCELADA' && iv.motivo_cancelamento && <p className="text-xs text-slate-500" data-testid={`interview-cancel-reason-${iv.id}`}>Motivo do cancelamento: {iv.motivo_cancelamento}</p>}
        {iv.status === 'NOVO_HORARIO' && iv.sugestao_data_hora && (
          <div className="rounded-lg bg-orange-50 border border-orange-200 p-2 text-sm" data-testid={`interview-suggestion-${iv.id}`}>
            <p className="text-orange-900 flex items-center gap-1"><Clock className="w-4 h-4" />Sugeriu: <strong>{fmtDateTime(iv.sugestao_data_hora)}</strong></p>
            {iv.sugestao_motivo && <p className="text-orange-800 text-xs mt-0.5">"{iv.sugestao_motivo}"</p>}
          </div>
        )}
      </div>
      <div className="flex sm:flex-col flex-wrap gap-2 shrink-0">
        {busy && <Loader2 className="w-4 h-4 animate-spin text-cyan-600" />}
        {iv.status === 'NOVO_HORARIO' && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} data-testid={`interview-accept-suggestion-${iv.id}`}
            onClick={() => run(() => interviewsAPI.acceptSuggestion(iv.id), 'Novo horário aceito. O candidato foi avisado.')}>
            <Check className="w-4 h-4 mr-1" />Aceitar horário
          </Button>
        )}
        {active && <Button size="sm" variant="outline" disabled={busy} onClick={() => setResched(true)} data-testid={`interview-reschedule-${iv.id}`}><CalendarClock className="w-4 h-4 mr-1" />Remarcar</Button>}
        {iv.status !== 'CANCELADA' && iv.status !== 'NOVO_HORARIO' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setResult(true)} data-testid={`interview-result-${iv.id}`}>
            <ClipboardCheck className="w-4 h-4 mr-1" />{iv.status === 'REALIZADA' ? 'Editar resultado' : 'Registrar resultado'}
          </Button>
        )}
        {active && <Button size="sm" variant="ghost" className="text-red-600" disabled={busy} onClick={cancel} data-testid={`interview-cancel-${iv.id}`}><XCircle className="w-4 h-4 mr-1" />Cancelar</Button>}
      </div>
      <ScheduleInterviewDialog open={resched} onOpenChange={setResched} interview={iv} candidateName={iv.candidate_name} onSaved={onChange} />
      <InterviewResultDialog open={result} onOpenChange={setResult} interview={iv} onSaved={onChange} />
    </div>
  );
};
